<div class="space-y-5">
  <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <h2 class="section-header-title">Reading Lists</h2>
      <p class="text-sm text-slate-600">
        Assign papers to group members and track reading progress.
      </p>
    </div>
    <button type="button" class="btn-primary" (click)="openCreateModal()">Create List</button>
  </div>

  @if (errorMessage()) {
    <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {{ errorMessage() }}
    </div>
  }

  @if (loading()) {
    <div class="space-y-3">
      @for (item of [1, 2, 3]; track item) {
        <div class="h-24 rounded-lg bg-slate-100 animate-pulse"></div>
      }
    </div>
  } @else {
    <section class="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div class="card-header">
        <h3 class="section-header-title">Assigned to me</h3>
      </div>
      @if (assignedToMe().length === 0) {
        <div class="px-6 py-8 text-center text-sm text-slate-600">
          No reading lists are assigned to you.
        </div>
      } @else {
        <div class="divide-y divide-slate-200">
          @for (list of assignedToMe(); track list.id) {
            <ng-container
              [ngTemplateOutlet]="listRow"
              [ngTemplateOutletContext]="{ list: list }"
            ></ng-container>
          }
        </div>
      }
    </section>

    <section class="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <div class="card-header">
        <h3 class="section-header-title">Assigned by me</h3>
      </div>
      @if (assignedByMe().length === 0) {
        <div class="px-6 py-8 text-center text-sm text-slate-600">
          No reading lists have been assigned by you yet.
        </div>
      } @else {
        <div class="divide-y divide-slate-200">
          @for (list of assignedByMe(); track list.id) {
            <ng-container
              [ngTemplateOutlet]="listRow"
              [ngTemplateOutletContext]="{ list: list }"
            ></ng-container>
          }
        </div>
      }
    </section>
  }
</div>

<ng-template #listRow let-list="list">
  <a
    [routerLink]="['/groups', groupId, 'reading-lists', list.id]"
    class="block px-5 py-4 transition-colors hover:bg-slate-50"
  >
    <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2">
          <h3 class="font-semibold text-gray-900">{{ list.title }}</h3>
          <span class="badge-primary">{{ statusText(list) }}</span>
        </div>
        <p class="mt-1 text-sm text-slate-600">
          Assigned to {{ list.assignedToName }} by {{ list.createdByName }}
        </p>
        @if (list.deadline) {
          <p class="mt-1 text-xs font-medium text-amber-700">
            Deadline {{ list.deadline | date: 'MMM d, yyyy' }}
          </p>
        }
      </div>

      <div class="w-full lg:w-64">
        <div class="mb-1 flex items-center justify-between text-xs font-semibold text-slate-600">
          <span>{{ list.readItems }} / {{ list.totalItems }} read</span>
          <span>{{ list.progressPercent }}%</span>
        </div>
        <div class="h-2 overflow-hidden rounded-full bg-slate-100">
          <div
            class="h-full rounded-full bg-secondary"
            [style.width.%]="list.progressPercent"
          ></div>
        </div>
      </div>
    </div>
  </a>
</ng-template>

<app-base-modal
  [open]="showCreateModal()"
  (openChange)="onModalOpenChange($event)"
  title="Create Reading List"
  size="2xl"
>
  <form
    class="flex max-h-[calc(100vh-8rem)] min-h-0 flex-col gap-6 overflow-y-auto pr-1"
    [formGroup]="createForm"
    (ngSubmit)="createReadingList()"
  >
    @if (createError()) {
      <div class="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {{ createError() }}
      </div>
    }

    <div>
      <label class="input-label" for="reading-list-title">Title</label>
      <input
        id="reading-list-title"
        type="text"
        class="input-field"
        formControlName="title"
        placeholder="e.g. Background papers"
      />
    </div>

    <section class="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <div class="mb-3 flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h3 class="text-sm font-semibold text-gray-900">Readers</h3>
          <p class="text-xs text-slate-500">
            Each selected member gets their own reading progress for this list.
          </p>
        </div>
        <div class="w-full lg:w-72">
          <label class="input-label" for="reading-list-reader-search">Search readers</label>
          <input
            id="reading-list-reader-search"
            type="search"
            class="input-field"
            placeholder="Name, email or role"
            [value]="readerQuery()"
            (input)="onReaderQueryChange($any($event.target).value)"
          />
        </div>
      </div>

      <div
        class="mb-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700"
      >
        @if (selectedAssigneeIds().length === 0) {
          Select one or more group members
        } @else {
          {{ selectedAssigneeIds().length }} selected:
          <span class="font-medium text-slate-900" data-testid="selected-assignees-summary">
            {{ selectedAssigneeNames() }}
          </span>
        }
      </div>

      @if (membersError()) {
        <p data-testid="members-error" class="mb-3 text-xs text-red-600">{{ membersError() }}</p>
      }

      @if (filteredAssignableMembers().length === 0) {
        <div
          class="rounded-lg border border-slate-200 bg-white px-4 py-6 text-center text-sm text-slate-600"
        >
          No matching readers found.
        </div>
      } @else {
        <div class="grid max-h-48 gap-2 overflow-y-auto sm:grid-cols-2">
          @for (member of filteredAssignableMembers(); track member.user.id) {
            <button
              type="button"
              class="flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors"
              [class.border-secondary]="isAssigneeSelected(member)"
              [class.bg-blue-50]="isAssigneeSelected(member)"
              [class.border-slate-200]="!isAssigneeSelected(member)"
              [class.bg-white]="!isAssigneeSelected(member)"
              (click)="toggleAssignee(member)"
              data-testid="assignee-toggle"
            >
              <span class="min-w-0">
                <span class="block truncate text-sm font-semibold text-slate-900">
                  {{ member.user.firstName }} {{ member.user.lastName }}
                </span>
                <span class="block truncate text-xs text-slate-500">
                  {{ member.user.email }} - {{ member.groupRole }}
                </span>
              </span>
              <span
                class="flex h-5 w-5 shrink-0 items-center justify-center rounded border text-xs font-bold"
                [class.border-secondary]="isAssigneeSelected(member)"
                [class.bg-secondary]="isAssigneeSelected(member)"
                [class.text-white]="isAssigneeSelected(member)"
                [class.border-slate-300]="!isAssigneeSelected(member)"
                [class.text-transparent]="!isAssigneeSelected(member)"
              >
                ✓
              </span>
            </button>
          }
        </div>
      }
    </section>

    <div>
      <label class="input-label" for="reading-list-description">Description</label>
      <textarea
        id="reading-list-description"
        class="input-field min-h-24"
        formControlName="description"
        placeholder="Optional context for the reader"
      ></textarea>
    </div>

    <div>
      <label class="input-label" for="reading-list-deadline">Deadline</label><br />
      <input
        id="reading-list-deadline"
        type="date"
        class="input-field md:max-w-xs"
        formControlName="deadline"
        [min]="minDeadline"
        [class.border-red-500]="
          createForm.controls.deadline.invalid && createForm.controls.deadline.touched
        "
      />
      @if (
        createForm.controls.deadline.invalid &&
        createForm.controls.deadline.touched &&
        createForm.controls.deadline.errors?.pastDate
      ) {
        <p class="mt-1 text-xs text-red-600">Deadline cannot be in the past.</p>
      }
    </div>

    <section
      class="flex min-h-[26rem] flex-col overflow-hidden rounded-lg border border-slate-200 bg-slate-50/60 p-4"
    >
      <div class="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 class="text-sm font-semibold text-gray-900">Papers</h3>
          <p class="text-xs text-slate-500">Only papers with verified full text can be added.</p>
        </div>
        <p class="text-xs font-medium text-slate-600">{{ selectedPapers().length }} selected</p>
      </div>

      <div
        class="grid min-h-0 flex-1 grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1.25fr)_minmax(320px,0.75fr)]"
      >
        <div class="flex min-h-0 min-w-0 flex-col">
          <label class="input-label" for="reading-list-paper-search">Add papers</label>
          <input
            id="reading-list-paper-search"
            type="search"
            class="input-field py-3 text-base"
            placeholder="Search group papers"
            [value]="paperQuery()"
            (input)="onPaperQueryChange($any($event.target).value)"
          />

          <div
            class="mt-3 min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200 bg-white"
          >
            @if (searchingPapers()) {
              <div class="px-4 py-6 text-center text-sm text-slate-600">Searching papers...</div>
            } @else if (paperResults().length === 0) {
              <div class="px-4 py-6 text-center text-sm text-slate-600">No papers found.</div>
            } @else {
              @for (paper of paperResults(); track paper.id) {
                <button
                  type="button"
                  class="block w-full border-b border-slate-100 px-4 py-3 text-left text-sm last:border-0 hover:bg-slate-50"
                  [class.bg-blue-50]="isPaperSelected(paper)"
                  (click)="togglePaper(paper)"
                >
                  <span class="block font-medium text-gray-900">{{
                    paper.title || 'Untitled'
                  }}</span>
                  <span class="block text-xs text-slate-500">
                    {{ paper.venue || 'Unknown venue' }}
                    @if (paper.year) {
                      <span> · {{ paper.year }}</span>
                    }
                  </span>
                </button>
              }
            }
          </div>
        </div>

        <div class="flex min-h-0 min-w-0 flex-col">
          <p class="input-label">Selected papers ({{ selectedPapers().length }})</p>
          <div class="min-h-0 flex-1 overflow-auto rounded-lg border border-slate-200 bg-white">
            @if (selectedPapers().length === 0) {
              <div class="px-4 py-6 text-center text-sm text-slate-600">
                Select at least one paper for this list.
              </div>
            } @else {
              @for (paper of selectedPapers(); track paper.id) {
                <div
                  class="flex items-start justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-0"
                >
                  <div class="min-w-0">
                    <p class="truncate text-sm font-medium text-gray-900">{{ paper.title }}</p>
                    <p class="text-xs text-slate-500">{{ paper.venue || 'Unknown venue' }}</p>
                  </div>
                  <button type="button" class="btn-small-text" (click)="removeSelectedPaper(paper)">
                    Remove
                  </button>
                </div>
              }
            }
          </div>
        </div>
      </div>
    </section>

    <div class="sticky bottom-0 flex justify-end gap-2 border-t border-slate-200 bg-white pt-4">
      <button type="button" class="btn-secondary" (click)="closeCreateModal()">Cancel</button>
      <button type="submit" class="btn-primary" [disabled]="creating()">
        {{ creating() ? 'Creating...' : 'Create and assign' }}
      </button>
    </div>
  </form>
</app-base-modal>
